import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import {
  getAccessRequests,
  createAccessRequest,
  approveAccessRequest,
  denyAccessRequest,
  withdrawAccessRequest,
} from '@/services/channelApi';
import type { CreateAccessRequestPayload } from '@/services/channelApi';

/**
 * Hook to fetch all pending access requests for a channel.
 * Enabled only when slug is provided. Silently returns empty array on 403/404.
 */
export function useChannelAccessRequests(slug: string | undefined) {
  return useQuery({
    queryKey: ['channel-access-requests', slug],
    enabled: !!slug,
    queryFn: async () => {
      if (!slug) return [];
      try {
        return await getAccessRequests(slug);
      } catch (err) {
        if (isAxiosError(err) && (err.response?.status === 403 || err.response?.status === 404)) {
          return [];
        }
        throw err;
      }
    },
  });
}

/** Mutation: submit a request to join a private channel. */
export function useCreateAccessRequest(slug: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateAccessRequestPayload) => createAccessRequest(slug, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['channel-access-requests', slug] });
    },
  });
}

/** Mutation: approve a pending request (owner or channel_admin). */
export function useApproveAccessRequest(slug: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (requestId: string) => approveAccessRequest(slug, requestId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['channel-access-requests', slug] });
      // Invalidate members list so the newly approved member appears immediately
      queryClient.invalidateQueries({ queryKey: ['channel-members', slug] });
    },
  });
}

/** Mutation: deny a pending request (owner or channel_admin). */
export function useDenyAccessRequest(slug: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (requestId: string) => denyAccessRequest(slug, requestId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['channel-access-requests', slug] });
    },
  });
}

/** Mutation: withdraw the current user's own request. */
export function useWithdrawAccessRequest(slug: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => withdrawAccessRequest(slug),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['channel-access-requests', slug] });
    },
  });
}
